import { resolveWorkspaceAssetUrl } from '../../lib/urls';
/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Widget Appearance Customization Tab Component (Task 10.3.6)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import { useWidgetStudioStore } from '../../stores/useWidgetStudioStore';
import { useI18n } from '../../hooks/useI18n';
import { Palette, MessageSquare, Bot, Sparkles, Check } from 'lucide-react';

const COLOR_PRESETS = [
  { name: 'Sophia Indigo', hex: '#6366f1' },
  { name: 'Emerald Edge', hex: '#10b981' },
  { name: 'Ocean Cyan', hex: '#06b6d4' },
  { name: 'Electric Violet', hex: '#8b5cf6' },
  { name: 'Rose Coral', hex: '#f43f5e' },
  { name: 'Amber Sunset', hex: '#f59e0b' },
  { name: 'Obsidian Slate', hex: '#1e293b' },
];

const AVATAR_PRESETS = [
  resolveWorkspaceAssetUrl('avatars/bot-avatar-01.png'),
  resolveWorkspaceAssetUrl('avatars/bot-avatar-02.png'),
  resolveWorkspaceAssetUrl('avatars/bot-avatar-03.png'),
  resolveWorkspaceAssetUrl('avatars/bot-avatar-04.png'),
];

export const WidgetAppearanceTab: React.FC = () => {
  const { t } = useI18n();
  const { config, updateAppearance } = useWidgetStudioStore();
  const { appearance } = config;

  return (
    <div className="space-y-6">
      {/* 1. Brand Identity & Naming */}
      <div className="space-y-4">
        <h3 className="text-sm font-heading font-bold text-foreground flex items-center gap-2">
          <Bot className="w-4 h-4 text-primary" />
          <span>{t('widget.botIdentityHeading')}</span>
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">
              {t('widget.botNameLabel')} <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              value={appearance.botName}
              onChange={(e) => updateAppearance({ botName: e.target.value })}
              placeholder="e.g. Sophia Assistant"
              className="w-full px-3 py-2 text-xs rounded-xl border border-surface-border bg-surface-elevated text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">{t('widget.botSubtitleLabel')}</label>
            <input
              type="text"
              value={appearance.botSubtitle}
              onChange={(e) => updateAppearance({ botSubtitle: e.target.value })}
              placeholder="e.g. Enterprise AI Assistant"
              className="w-full px-3 py-2 text-xs rounded-xl border border-surface-border bg-surface-elevated text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
        </div>

        {/* Avatar Presets */}
        <div className="space-y-2">
          <label className="text-xs font-semibold text-foreground">{t('widget.botAvatarLabel')}</label>
          <div className="flex items-center gap-3">
            {AVATAR_PRESETS.map((url, index) => {
              const isSelected = appearance.avatarUrl === url;
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => updateAppearance({ avatarUrl: url })}
                  className={`w-11 h-11 rounded-full border-2 flex items-center justify-center relative overflow-hidden transition-all ${
                    isSelected
                      ? 'border-primary shadow-glow-primary scale-105'
                      : 'border-surface-border hover:border-foreground-muted/60 opacity-80 hover:opacity-100'
                  }`}
                >
                  <div className="w-full h-full bg-gradient-to-tr from-primary/30 to-accent/30 flex items-center justify-center font-bold text-xs text-primary">
                    AI-{index + 1}
                  </div>
                  {isSelected && (
                    <div className="absolute inset-0 bg-primary/25 flex items-center justify-center text-white">
                      <Check className="w-4 h-4 stroke-[3]" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <hr className="border-surface-border" />

      {/* 2. Brand Color & Theme */}
      <div className="space-y-4">
        <h3 className="text-sm font-heading font-bold text-foreground flex items-center gap-2">
          <Palette className="w-4 h-4 text-primary" />
          <span>{t('widget.brandColorHeading')}</span>
        </h3>

        {/* Color Presets */}
        <div className="space-y-2">
          <label className="text-xs font-semibold text-foreground">
            {t('widget.primaryColorLabel')}
          </label>
          <div className="flex flex-wrap items-center gap-2.5">
            {COLOR_PRESETS.map((preset) => {
              const isSelected = appearance.primaryColor.toLowerCase() === preset.hex.toLowerCase();
              return (
                <button
                  key={preset.hex}
                  type="button"
                  onClick={() => updateAppearance({ primaryColor: preset.hex })}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                    isSelected
                      ? 'border-primary bg-primary/10 text-foreground ring-1 ring-primary'
                      : 'border-surface-border bg-surface hover:bg-surface-elevated text-foreground-muted'
                  }`}
                >
                  <span
                    className="w-3.5 h-3.5 rounded-full flex-shrink-0 shadow-sm"
                    style={{ backgroundColor: preset.hex }}
                  />
                  <span>{preset.name}</span>
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-3 pt-2">
            <span className="text-xs text-foreground-muted font-mono">{t('widget.customHexLabel')}</span>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={appearance.primaryColor}
                onChange={(e) => updateAppearance({ primaryColor: e.target.value })}
                className="w-7 h-7 rounded-lg cursor-pointer border border-surface-border bg-transparent"
              />
              <input
                type="text"
                value={appearance.primaryColor}
                onChange={(e) => updateAppearance({ primaryColor: e.target.value })}
                className="w-24 px-2 py-1 text-xs font-mono rounded-lg border border-surface-border bg-surface-elevated text-foreground uppercase"
              />
            </div>
          </div>
        </div>

        {/* Position & Launcher Text */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">{t('widget.chatPositionLabel')}</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => updateAppearance({ position: 'bottom-right' })}
                className={`px-3 py-2 text-xs rounded-xl border text-center font-medium transition-colors ${
                  appearance.position === 'bottom-right'
                    ? 'border-primary bg-primary text-primary-fg font-bold'
                    : 'border-surface-border bg-surface hover:bg-surface-elevated text-foreground-muted'
                }`}
              >
                {t('widget.bottomRight')}
              </button>
              <button
                type="button"
                onClick={() => updateAppearance({ position: 'bottom-left' })}
                className={`px-3 py-2 text-xs rounded-xl border text-center font-medium transition-colors ${
                  appearance.position === 'bottom-left'
                    ? 'border-primary bg-primary text-primary-fg font-bold'
                    : 'border-surface-border bg-surface hover:bg-surface-elevated text-foreground-muted'
                }`}
              >
                {t('widget.bottomLeft')}
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">{t('widget.launcherTextLabel')}</label>
            <input
              type="text"
              value={appearance.launcherText}
              onChange={(e) => updateAppearance({ launcherText: e.target.value })}
              placeholder="e.g. Chat with us"
              className="w-full px-3 py-2 text-xs rounded-xl border border-surface-border bg-surface-elevated text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
        </div>
      </div>

      <hr className="border-surface-border" />

      {/* 3. Greeting Message */}
      <div className="space-y-3">
        <h3 className="text-sm font-heading font-bold text-foreground flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-primary" />
          <span>{t('widget.initialGreetingHeading')}</span>
        </h3>
        <p className="text-xs text-foreground-muted">
          {t('widget.initialGreetingDesc')}
        </p>

        <textarea
          rows={3}
          value={appearance.greetingMessage}
          onChange={(e) => updateAppearance({ greetingMessage: e.target.value })}
          placeholder={t('widget.welcomeMessage')}
          className="w-full p-3 text-xs rounded-xl border border-surface-border bg-surface-elevated text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 leading-relaxed"
        />

        <div className="flex items-center gap-2 text-[11px] text-foreground-muted">
          <Sparkles className="w-3.5 h-3.5 text-accent" />
          <span>{t('widget.greetingTip')}</span>
        </div>
      </div>
    </div>
  );
};
